import { PAGE_SIZE_DIMENSIONS, PageMargins, PageSize, PageSizePreset } from '~/lib/services/export/gotenberg'
import { GanttSettings } from '~/composables/gantt/useGanttSettings'
import { useI18n } from 'vue-i18n'
import useUser from '~/composables/useUser'
import { DateFormatsEnum, formatDate } from '~/helper/date'
import { ActivityRead } from '@gridcon-energy/schaltungstool-backend'
import { z } from 'zod'
import type { NetworkColoringStorage } from '~/composables/coloring/useSVGNetworkStateColoring'
import type { TopologyChangesGroupingEnum } from '~/lib/topologies'
import { NetworkDisplayModeEnum } from '~/lib/equipments'
import type { AppErrorMessage } from '~/plugins/vue-plugins/fetch-error-handler'

/**
 * Enum defining all available report sections types
 */
export enum ReportSectionTypeEnum {
  FRONT_MATTER = 'front-matter',
  GANTT_CHART = 'gantt-chart',
  GANTT_COVER = 'gantt-legend',
  SWITCHINGS_TABLE = 'switchings-table',
  EQUIPMENTS_TABLE = 'equipments-table',
  PROJECTS_TABLE = 'projects-table',
  SUBPROJECTS_TABLE = 'subprojects-table',
  TOPOLOGY_MATRIX = 'topology-matrix',
  TOPOLOGY_TABLE = 'topology-names',
  NETWORK_TOPOLOGIES = 'network-topologies',
  NETWORK_STATE_START = 'network-state-start',
  NETWORK_STATE_END = 'network-state-end',
  NETWORK_EQUIPMENT_STATUS = 'network-equipment-status',
  CUSTOM = 'custom-section',
}

export const ReportSectionIcons: { [key in ReportSectionTypeEnum]: string } = {
  [ReportSectionTypeEnum.FRONT_MATTER]: 'mdi-book-open-page-variant',
  [ReportSectionTypeEnum.GANTT_CHART]: '$GanttChart',
  [ReportSectionTypeEnum.GANTT_COVER]: 'mdi-book-open-page-variant',
  [ReportSectionTypeEnum.SWITCHINGS_TABLE]: 'mdi-electric-switch',
  [ReportSectionTypeEnum.EQUIPMENTS_TABLE]: 'mdi-transmission-tower',
  [ReportSectionTypeEnum.PROJECTS_TABLE]: '$LightingBoltCity',
  [ReportSectionTypeEnum.SUBPROJECTS_TABLE]: '$LightingBoltHome',
  [ReportSectionTypeEnum.TOPOLOGY_MATRIX]: '$GridOn',
  [ReportSectionTypeEnum.TOPOLOGY_TABLE]: 'mdi-tag-multiple',
  [ReportSectionTypeEnum.NETWORK_TOPOLOGIES]: 'mdi-chart-timeline',
  [ReportSectionTypeEnum.NETWORK_STATE_START]: 'mdi-page-first',
  [ReportSectionTypeEnum.NETWORK_STATE_END]: 'mdi-page-last',
  [ReportSectionTypeEnum.NETWORK_EQUIPMENT_STATUS]: '$TransmissionTowerBolt',
  [ReportSectionTypeEnum.CUSTOM]: 'mdi-file-code-outline',
}

export const ReportSectionOrder = [
  ReportSectionTypeEnum.FRONT_MATTER,
  ReportSectionTypeEnum.SWITCHINGS_TABLE,
  ReportSectionTypeEnum.EQUIPMENTS_TABLE,
  ReportSectionTypeEnum.TOPOLOGY_TABLE,
  ReportSectionTypeEnum.NETWORK_STATE_START,
  ReportSectionTypeEnum.NETWORK_STATE_END,
  ReportSectionTypeEnum.NETWORK_EQUIPMENT_STATUS,
  ReportSectionTypeEnum.GANTT_COVER,
  ReportSectionTypeEnum.GANTT_CHART,
]

export enum PageOrientation {
  PORTRAIT = 'portrait',
  LANDSCAPE = 'landscape',
}

type SectionSettingsPDF = {
  orientation: PageOrientation
  size: PageSizePreset | PageSize
  /**
   * Margins in inches
   */
  margins: Required<PageMargins>
  scaling: number
  showPageNumber: boolean
}

// Settings for Cover/TOC section
export const DefaultLogo: HTMLImageElement = document.createElement('img')
DefaultLogo.src = '/transnetbw.svg'
export const DefaultLogoSize = 1.25 // cm
export type FrontMatterSettings = {
  coverLogo?: SVGSVGElement | HTMLImageElement
  coverLogoSize: number
  lines?: 'vertical' | 'horizontal'

  // TOC - Inhaltsverzeichnis
  showTOC: boolean
  titleTOC?: string

  // Changelog
  showChangelog: boolean
}

export type TOCRecord = {
  id: string
  title: string
  pageNumber: string
  children?: Omit<TOCRecord, 'section'>[]
  section: ReportSection
}

export type NetworkTitleBlock = {
  // internal
  _type: NetworkDisplayModeEnum
  // Logo
  logo_of_owner: SVGSVGElement | HTMLImageElement // Default report svg
  // Erstellt durch
  created_by: string // Default changelog last entry
  // Geprüft von
  approved_by: string // Default changelog last entry
  // Dokumentart
  document_type: string // : Zielnetz: „Zielnetz“, Startnetz: „Startnetz“, Topologien: „Umbauablaufplan“
  // Titel
  title: string
  // Projekt
  project: string // Default activity name
  // Sachnummer
  item_number: string //
  // Version
  version: string // Changelog Version
  // Datum
  date: string // Changelog Date version
  // Entwurf
  draft: boolean
}

/**
 * Runtime / page-specific values
 */
export type NetworkTitleBlockPage = NetworkTitleBlock & {
  // Blattgröße
  page_size: string // A4 ...
  // Blatt
  page_number: string // x/y
  // Sub Title
  sub_title: string // Topology Schrittname
}
export function isNetworkTitleBlockPage(item: Partial<NetworkTitleBlockPage>): item is NetworkTitleBlockPage {
  return 'page_size' in item && 'page_number' in item && 'sub_title' in item
}

export type NetworkTopologiesSettings = {
  pagesAppendPeriodDates: boolean
  showTopologiesDiff: boolean
  topologyChangesGrouping: TopologyChangesGroupingEnum
  svgTitleBlock: NetworkTitleBlock
  // TopologyPeriod.step values excluded from rendering; empty means all periods are included
  excludedPeriodSteps: number[]
}

export type NetworkStateSettings = {
  showEquipments: boolean
  svgTitleBlock: NetworkTitleBlock
  coloring: NetworkColoringStorage
}
export type NetworkEquipmentStatusSettings = {
  showEquipments: boolean
  svgTitleBlock: NetworkTitleBlock
}

export type TopologyMatrixSettings = {
  periodsPerPage: number
  wholeTable: boolean
}

export type ReportSectionSettings<T extends ReportSectionTypeEnum> = T extends ReportSectionTypeEnum.FRONT_MATTER
  ? FrontMatterSettings
  : T extends ReportSectionTypeEnum.GANTT_CHART
    ? GanttSettings
    : T extends ReportSectionTypeEnum.NETWORK_TOPOLOGIES
      ? NetworkTopologiesSettings
      : T extends ReportSectionTypeEnum.NETWORK_STATE_START | ReportSectionTypeEnum.NETWORK_STATE_END
        ? NetworkStateSettings
        : T extends ReportSectionTypeEnum.NETWORK_EQUIPMENT_STATUS
          ? NetworkEquipmentStatusSettings
          : T extends ReportSectionTypeEnum.TOPOLOGY_MATRIX
            ? TopologyMatrixSettings
            : {}
export type ReportSectionState<T extends ReportSectionTypeEnum> = T extends ReportSectionTypeEnum.TOPOLOGY_MATRIX
  ? { size: PageSize }
  : {}

export type CreateSectionParams<T extends ReportSectionTypeEnum> = {
  settings: ReportSectionSettings<T>
  settingsPDF?: Partial<SectionSettingsPDF>
  state: ReportSectionState<T>
}

export enum PreviewProgressEnum {
  COMPONENT = 'component',
  PDF = 'pdf',
  SPLIT = 'split',
  IMAGES = 'images',
  PAGES = 'pages',
}

export type PageItemRendered = {
  pdf: File
  image: File
}

export type PageNumberItem = {
  start: number
  end: number
  range: string
}
export type SectionPageItem = PageItemRendered & {
  imageUrl: string
  include: boolean
  title: string
  index: number
}

export type PreviewError = {
  message: string
  details: AppErrorMessage
}

export type ReportSection<T extends ReportSectionTypeEnum = ReportSectionTypeEnum, C = unknown> = {
  id: string
  title: string
  type: T
  settings: ReportSectionSettings<T>
  settingsPDF: SectionSettingsPDF
  // settings & settingsPDF are used for a watching rerender
  state: ReportSectionState<T>
  remove: () => void
  preview: {
    progress: {
      step: PreviewProgressEnum
      stepTitle: string
      current: number
      total: number
    }
    pending: boolean
    pages: SectionPageItem[]
    error: null | PreviewError
    renderComponent: () => Promise<Disposable & C>
    renderPDF: (component: C) => Promise<File>
    renderPreview: () => Promise<boolean>
    renderPreviewCurrent: null | Promise<boolean>
    purgePages: () => void // revokes Object URLs and clears page array
  }
}

export type ReportMetadata = {
  title: string
  author: string
  subject: string
  createdAt: string // ISO date string
  keywords: string[]
}

export type HeaderFooterConfig = {
  enabled: boolean
  left: string
  right: string
  center?: string
}

// Changelog types
export type ReportConfig = {
  metadata: ReportMetadata
  changelog: ChangelogRecord[]
  header: HeaderFooterConfig
  footer: HeaderFooterConfig
  zoom: ZoomLevelEnum
}

/**
 * Values are grid columns
 */
export enum ZoomLevelEnum {
  one = 12,
  two = 6,
  three = 4,
  four = 3,
}

const HeaderFooterSchema = z.object({
  enabled: z.boolean(),
  left: z.string(),
  right: z.string(),
  center: z.string(),
})
export const ReportConfigSchema = z.object({
  metadata: z.object({
    title: z.string(),
    author: z.string(),
    subject: z.string(),
    createdAt: z.string(),
    keywords: z.array(z.string()),
  }),
  changelog: z.array(
    z.object({
      id: z.string(),
      version: z.string(),
      date: z.string(),
      description: z.string(),
      author: z.string(),
      auditedBy: z.string().optional(),
    }),
  ),

  header: HeaderFooterSchema,
  footer: HeaderFooterSchema,
  zoom: z.nativeEnum(ZoomLevelEnum),
}) satisfies z.ZodType<ReportConfig>

export type ChangelogRecord = {
  id: string
  version: string
  date: string // ISO date string
  description: string
  author: string
  auditedBy?: string
}

export type RenderPipeline<T extends ReportSectionTypeEnum, C> = {
  component: (section: ReportSection<T>) => Promise<C> | C
  pdf: (section: ReportSection<T>, component: C) => Promise<File>
  pageTitle?: (item: PageItemRendered, index: number, all: PageItemRendered[], section: ReportSection<T>) => string
}

export function px2inch(px: number) {
  const dpr = 1 / window.devicePixelRatio || 1
  return px / (96 * dpr)
}

export function inch2px(inch: number) {
  const dpr = window.devicePixelRatio || 1
  return inch * (96 * dpr)
}

export function inch2cm(inch: number) {
  return inch * 2.54
}

export function cm2inch(cm: number) {
  return cm / 2.54
}

export const DefaultSectionSettingsPDF: SectionSettingsPDF = {
  orientation: PageOrientation.PORTRAIT,
  size: PageSizePreset.A4,
  margins: {
    top: cm2inch(1),
    bottom: cm2inch(1),
    left: cm2inch(1),
    right: cm2inch(1),
  },
  scaling: 1,
  showPageNumber: true,
}

export function createDefaultConfig(activity: ActivityRead): ReportConfig {
  const { t } = useI18n()
  const { user } = useUser()
  const title = activity.name
  const subject = t('ReportCoverSubject')
  const keywords = [title, subject, t('AppOwner')]
  const createdAt = new Date().toISOString()

  return {
    changelog: [],
    metadata: {
      title,
      createdAt,
      author: user.value?.name ?? t('AppOwner'),
      subject,
      keywords,
    },
    header: {
      enabled: true,
      left: title,
      right: formatDate(createdAt, DateFormatsEnum.EUROPE_LONG),
      center: '',
    },
    footer: {
      enabled: true,
      left: '', // This will be populated per section
      right: '', // Page number is handled automatically
      center: '',
    },
    zoom: ZoomLevelEnum.three,
  }
}

export function isLandscape(orientation: PageOrientation): boolean {
  return orientation === PageOrientation.LANDSCAPE
}

export function isSectionType<T extends ReportSectionTypeEnum>(
  section: ReportSection,
  type: T,
): section is ReportSection<T> {
  return section.type === type
}

export function isCoverSection(section: ReportSection) {
  return (
    isSectionType(section, ReportSectionTypeEnum.FRONT_MATTER) ||
    isSectionType(section, ReportSectionTypeEnum.GANTT_COVER)
  )
}

export function asSectionOrNull<T extends ReportSectionTypeEnum>(
  section: ReportSection,
  type: T,
): ReportSection<T> | null {
  return isSectionType(section, type) ? section : null
}

/**
 * Builds the TOC records from the sections and their page numbers.
 * Shared by the printed TOC (ReportTableOfContents.vue) and the PDF outline
 * (printPages), so both always show the same structure.
 */
export function buildTOCRecords(
  sections: ReportSection[],
  pageNumberMap: Map<SectionPageItem | ReportSection, PageNumberItem>,
): TOCRecord[] {
  const records: TOCRecord[] = []
  for (const section of sections) {
    if (isSectionType(section, ReportSectionTypeEnum.FRONT_MATTER)) {
      continue
    }
    const children: Omit<TOCRecord, 'section'>[] = []
    for (const page of section.preview.pages) {
      if (!page.include) {
        continue
      }
      children.push({
        id: page.title,
        title: page.title,
        pageNumber: String(pageNumberMap.get(page)?.start || ''),
      })
    }
    if (children.length === 0) {
      continue
    }
    if (children.length === 1) {
      children.length = 0
    }
    records.push({
      id: section.id,
      title: section.title,
      section,
      pageNumber: String(pageNumberMap.get(section)?.start || ''),
      children,
    })
  }
  return records
}

export function sizeToDimensions(size: PageSizePreset | PageSize): PageSize {
  if (typeof size === 'string') {
    return PAGE_SIZE_DIMENSIONS[size]
  }
  return size
}

export function formatSize(section: ReportSection, short: boolean = false): string {
  let size = sizeToDimensions(section.settingsPDF.size)

  if (
    isSectionType(section, ReportSectionTypeEnum.TOPOLOGY_MATRIX) &&
    section.state.size &&
    section.settings.wholeTable
  ) {
    size = section.state.size
    if (short) {
      return 'A?'
    }
  }
  const prepend = typeof section.settingsPDF.size === 'string' ? section.settingsPDF.size : ''

  if (short) {
    return prepend
  }
  const { width, height } = size

  const w = typeof width === 'number' ? `${inch2cm(width).toFixed(1)}cm` : width
  const h = typeof height === 'number' ? `${inch2cm(height).toFixed(1)}cm` : height

  let dimensionsString = `h: ${h} - w: ${w}`
  if (prepend) {
    dimensionsString = prepend + ' | ' + dimensionsString
  }
  return dimensionsString
}
